import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  ArrowRight,
  Activity,
  Globe,
  Server,
  ShieldCheck,
  Mail,
  Lock,
} from "lucide-react";
import { useStore } from "../store/useStore";
import { Button } from "../components/ui/Button";
import { cn } from "../utils/helpers";
import { DUMMY_USERS } from "../data/dummyUsers";
import { localStorageUtils, STORAGE_KEYS } from "../utils/localStorage";

// --- CONFIG & DATA ---
const LOGIN_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80",
    title: "Global Intelligence",
    subtitle: "Real-time market data across 42 countries.",
  },
  {
    url: "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=2000&q=80",
    title: "Premium Assets",
    subtitle: "Managing over $4.2B in commercial real estate.",
  },
  {
    url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=2000&q=80",
    title: "Data Driven",
    subtitle: "AI-powered forecasting for smarter investments.",
  },
];

const SYSTEM_STATS = [
  {
    label: "System Status",
    value: "Operational",
    icon: Activity,
    color: "text-emerald-400",
  },
  {
    label: "Active Nodes",
    value: "1,420",
    icon: Server,
    color: "text-indigo-400",
  },
  {
    label: "Global Latency",
    value: "24ms",
    icon: Globe,
    color: "text-blue-400",
  },
];

const emailSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});

export default function Login() {
  const login = useStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const emailForm = useForm({ resolver: zodResolver(emailSchema) });

  useEffect(() => {
    const timer = setInterval(
      () => setCurrentImageIndex((p) => (p + 1) % LOGIN_IMAGES.length),
      6000,
    );
    return () => clearInterval(timer);
  }, []);

  const onEmailLogin = async (data: any) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    const user = DUMMY_USERS.find(
      (u) => u.email === data.email && u.password === data.password,
    );
    if (user) {
      localStorageUtils.set(STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
      login({ name: user.name, email: user.email, uid: user.uid });
    } else {
      emailForm.setError("email", { message: "Invalid credentials" });
    }
    setIsLoading(false);
  };

  return (
    <div className="h-screen w-full flex bg-slate-950 font-sans overflow-hidden text-slate-100 selection:bg-indigo-500/30 relative">
      {/* Cinematic Noise Texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* LEFT SIDE: Immersive Visuals */}
      <div className="hidden lg:flex w-7/12 relative overflow-hidden h-full">
        {/* Animated Background Slider */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <img
              src={LOGIN_IMAGES[currentImageIndex].url}
              className="w-full h-full object-cover opacity-70"
              alt="background"
            />

            {/* Seamless Blending Gradient */}
            {/* Right edge blends into slate-950 */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-950/20 to-slate-950" />
            {/* Bottom edge fade */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col justify-end p-20 w-full max-w-4xl">
          <motion.div
            key={`text-${currentImageIndex}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <div className="flex gap-2 mb-8">
              {LOGIN_IMAGES.map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "h-1 rounded-full transition-all duration-700 ease-out",
                    i === currentImageIndex
                      ? "w-16 bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)]"
                      : "w-4 bg-slate-700",
                  )}
                />
              ))}
            </div>
            <h2 className="text-7xl font-bold text-white mb-6 tracking-tighter leading-none drop-shadow-2xl">
              {LOGIN_IMAGES[currentImageIndex].title}
            </h2>
            <p className="text-2xl text-slate-300 font-light leading-relaxed border-l-2 border-indigo-500/50 pl-6 backdrop-blur-sm bg-slate-950/10 rounded-r-lg py-2">
              {LOGIN_IMAGES[currentImageIndex].subtitle}
            </p>
          </motion.div>
        </div>
      </div>

      {/* RIGHT SIDE: Login Interface */}
      <div className="w-full lg:w-5/12 flex flex-col justify-center items-center p-8 lg:p-16 relative h-full bg-slate-950 overflow-y-auto">
        {/* Subtle Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-900/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="w-full max-w-md space-y-10 z-10 relative">
          {/* Header */}
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block relative"
            >
              <div className="absolute inset-0 bg-indigo-500 blur-md opacity-40 rounded-full"></div>
              <div className="relative w-20 h-20 p-1 rounded-full bg-gradient-to-b from-slate-700 to-slate-900 border border-slate-700 shadow-2xl overflow-hidden">
                <img
                  src="/ddre-logo.jpeg"
                  alt="Logo"
                  className="w-full h-full rounded-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-500"
                />
              </div>
            </motion.div>

            <div>
              <p className="text-slate-400 mt-2 text-xs font-semibold uppercase tracking-[0.2em] opacity-70">
                Secured Enterprise Gateway
              </p>
            </div>
          </div>

          {/* Main Form Area */}
          <div className="relative">
            <motion.form
              key="email"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              onSubmit={emailForm.handleSubmit(onEmailLogin)}
              className="space-y-6"
            >
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Corporate ID
                </label>
                <div className="relative group">
                  <Mail
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors"
                    size={18}
                  />
                  <input
                    {...emailForm.register("email")}
                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-900/50 border border-slate-800 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 outline-none text-white placeholder:text-slate-700 transition-all hover:bg-slate-900"
                    placeholder="admin@ddre.com"
                  />
                </div>
                {emailForm.formState.errors.email && (
                  <p className="text-rose-400 text-xs ml-1 flex items-center gap-1 font-medium">
                    <ShieldCheck size={10} />{" "}
                    {emailForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Secure Key
                </label>
                <div className="relative group">
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors"
                    size={18}
                  />
                  <input
                    {...emailForm.register("password")}
                    type="password"
                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-900/50 border border-slate-800 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 outline-none text-white placeholder:text-slate-700 transition-all hover:bg-slate-900"
                    placeholder="••••••••"
                  />
                </div>
                {emailForm.formState.errors.password && (
                  <p className="text-rose-400 text-xs ml-1 font-medium">
                    {emailForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <Button
                disabled={isLoading}
                className="w-full py-4 text-sm font-bold bg-indigo-600 hover:bg-indigo-500 shadow-[0_0_20px_rgba(79,70,229,0.3)] mt-2 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] border border-indigo-500/50"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    Authenticate Session <ArrowRight size={16} />
                  </span>
                )}
              </Button>
            </motion.form>
          </div>

          {/* System Metrics Footer */}
          <div className="grid grid-cols-3 gap-3 pt-8 border-t border-slate-800/50">
            {SYSTEM_STATS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1 }}
                className="group relative bg-slate-900/40 p-3 rounded-xl border border-slate-800/60 text-center overflow-hidden hover:bg-slate-800/60 transition-colors backdrop-blur-sm"
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <s.icon
                  size={16}
                  className={cn(
                    "mx-auto mb-2 opacity-70 group-hover:opacity-100 transition-opacity drop-shadow-md",
                    s.color,
                  )}
                />
                <p className="text-[9px] font-bold uppercase text-slate-500 group-hover:text-slate-300 tracking-wider transition-colors">
                  {s.label}
                </p>
                <p className="text-xs font-bold text-slate-300 group-hover:text-white mt-1 transition-colors">
                  {s.value}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center pt-2">
            <p className="text-[10px] text-slate-600 flex items-center justify-center gap-1.5 opacity-60 hover:opacity-100 transition-opacity cursor-default">
              <ShieldCheck size={10} className="text-emerald-500" />
              256-bit SSL Encrypted Connection
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
